import { z } from "zod";
import {
    RefValueSchema,
    AuditLogDiffSchema,
    AuditLogQuerySchema,
    AuditLogResponseSchema,
    AuditLogSchema,
    AuditLogFilterSchema,
    AuditLogSourceTypeSchema,
    AuditLogRuleConditionSchema,
    ActivityFilterSchema,
    SourcePredicateSchema,
    HistoryFilterSchema,
    HistoryQuerySchema,
    HistoryResponseSchema,
    HistoryItemSchema,
    HistoryDailyCountSchema,
    HistoryCountsSeriesSchema,
    HistoryCountsSeriesBreakdownSchema,
    HistoryCountsPeopleBreakdownSchema,
    HistoryDailyCountsQuerySchema,
    HistoryDailyCountsResponseSchema,
    HistoryDetailsQuerySchema,
    HistoryDetailsItemSchema,
    ResourceSchema,
    HistoryDetailsResponseSchema,
    HistoryDetailsGroupSchema,
    GroupedHistoryDetailsResponseSchema,
    GroupByDiffValueSchema,
    SourceIdGroupSchema,
    SourceIdGroupedResponseSchema,
    CoaTemplateApplyHistoryEventRefSchema,
    CoaTemplateApplyHistoryPresentationSchema,
    ScheduleActivityHistoryPresentationSchema,
    ReportSettingsUpdateHistoryPresentationSchema,
    ReportTemplateChangeHistoryPresentationSchema,
    HistoryPresentationSchema,
    RelatedHistoryResourceSchema,
} from "@common/contracts/auditLog.contract";
import type {
    OrganizationHistoryDailyCountsQuerySchema,
    OrganizationHistoryDetailsQuerySchema,
    OrganizationHistoryFilterSchema,
    OrganizationHistoryQuerySchema,
} from "@common/contracts/organization-audit-log.contract";
import {
    APIProcessName,
    SQSProcessName,
    ActivityProcessName,
    RPCProcessName,
    TestProcessName,
} from "@common/constants/process-names";
import { RuleGroupType } from "./transactionRule";

export type RefValue = z.infer<typeof RefValueSchema>;
export type AuditLogQuery = z.infer<typeof AuditLogQuerySchema>;
export type AuditLogResponse = z.infer<typeof AuditLogResponseSchema>;
export type AuditLogDiff = z.infer<typeof AuditLogDiffSchema>;
export type AuditLog = z.infer<typeof AuditLogSchema>;
export type AuditLogFilter = z.infer<typeof AuditLogFilterSchema>;
export type HistoryFilter = z.infer<typeof HistoryFilterSchema>;
export type RelatedHistoryResource = z.infer<
    typeof RelatedHistoryResourceSchema
>;
export type OrganizationHistoryFilter = z.infer<
    typeof OrganizationHistoryFilterSchema
>;
export type OrganizationHistoryQuery = z.infer<
    typeof OrganizationHistoryQuerySchema
>;
export type OrganizationHistoryDailyCountsQuery = z.infer<
    typeof OrganizationHistoryDailyCountsQuerySchema
>;
export type OrganizationHistoryDetailsQuery = z.infer<
    typeof OrganizationHistoryDetailsQuerySchema
>;
export type AuditLogSourceType = z.infer<typeof AuditLogSourceTypeSchema>;
export type SourcePredicate = z.infer<typeof SourcePredicateSchema>;
export type ActivityFilter = z.infer<typeof ActivityFilterSchema>;
export type HistoryQuery = z.infer<typeof HistoryQuerySchema>;
export type HistoryResponse = z.infer<typeof HistoryResponseSchema>;
export type HistoryItem = z.infer<typeof HistoryItemSchema>;
export type HistoryDailyCount = z.infer<typeof HistoryDailyCountSchema>;
export type HistoryCountsSeries = z.infer<typeof HistoryCountsSeriesSchema>;
export type HistoryCountsSeriesBreakdown = z.infer<
    typeof HistoryCountsSeriesBreakdownSchema
>;
export type HistoryCountsPeopleBreakdown = z.infer<
    typeof HistoryCountsPeopleBreakdownSchema
>;
export type HistoryDailyCountsQuery = z.infer<
    typeof HistoryDailyCountsQuerySchema
>;
export type HistoryDailyCountsResponse = z.infer<
    typeof HistoryDailyCountsResponseSchema
>;
export type CoaTemplateApplyHistoryEventRef = z.infer<
    typeof CoaTemplateApplyHistoryEventRefSchema
>;
export type CoaTemplateApplyHistoryPresentation = z.infer<
    typeof CoaTemplateApplyHistoryPresentationSchema
>;
export type ScheduleActivityHistoryPresentation = z.infer<
    typeof ScheduleActivityHistoryPresentationSchema
>;
export type ReportSettingsUpdateHistoryPresentation = z.infer<
    typeof ReportSettingsUpdateHistoryPresentationSchema
>;
export type ReportTemplateChangeHistoryPresentation = z.infer<
    typeof ReportTemplateChangeHistoryPresentationSchema
>;
export type HistoryPresentation = z.infer<typeof HistoryPresentationSchema>;
export type HistoryDetailsQuery = z.infer<typeof HistoryDetailsQuerySchema>;
export type Resource = z.infer<typeof ResourceSchema>;
export type HistoryDetailsItem = z.infer<typeof HistoryDetailsItemSchema>;
export type HistoryDetailsResponse = z.infer<
    typeof HistoryDetailsResponseSchema
>;
export type HistoryDetailsGroup = z.infer<typeof HistoryDetailsGroupSchema>;
export type GroupedHistoryDetailsResponse = z.infer<
    typeof GroupedHistoryDetailsResponseSchema
>;
export type GroupByDiffValue = z.infer<typeof GroupByDiffValueSchema>;
export type AuditLogRuleCondition = z.infer<typeof AuditLogRuleConditionSchema>;
export type SourceIdGroup = z.infer<typeof SourceIdGroupSchema>;
export type SourceIdGroupedResponse = z.infer<
    typeof SourceIdGroupedResponseSchema
>;

export function isHistoryDetailsResponse(
    response:
        | HistoryDetailsResponse
        | GroupedHistoryDetailsResponse
        | SourceIdGroupedResponse,
): response is HistoryDetailsResponse {
    return "data" in response && "page" in response;
}

export function isSourceIdGroupedResponse(
    response:
        | HistoryDetailsResponse
        | GroupedHistoryDetailsResponse
        | SourceIdGroupedResponse,
): response is SourceIdGroupedResponse {
    return "groups" in response && !("details" in response);
}

export enum AuditLogChangeType {
    CREATE = "CREATE",
    UPDATE = "UPDATE",
    DELETE = "DELETE",
    DOWNLOAD = "DOWNLOAD",
}

export enum AuditLogScopeType {
    WORKSPACE = "workspace",
    ORGANIZATION = "organization",
}

export interface AuditLogScope {
    scopeType: AuditLogScopeType;
    scopeId: string;
}

export enum ActorType {
    USER = "USER",
    SUPPORT = "SUPPORT",
    SYSTEM = "SYSTEM",
    OPS = "OPS",
    PERSONAL_ACCESS_TOKEN = "PERSONAL_ACCESS_TOKEN",
    MCP_OAUTH = "MCP_OAUTH",
    ORGANIZATION = "ORGANIZATION",
    MCP = "MCP",
    CLI = "CLI",
    AGENT = "AGENT",
}

// Where a change came through, independent of who made it (`ActorType`) and
// how they authenticated (`metadata.authMethod`). Persisted to
// `audit_logs.channel`.
export enum AuditChannel {
    WEB = "web",
    MCP = "mcp",
    CLI = "cli",
    API = "api",
    AGENT = "agent",
    SYSTEM = "system",
}

export enum AuditAuthMethod {
    PAT = "pat",
    OAUTH = "oauth",
}

export enum ActorKind {
    INTEGRATION = "integration",
    RULE = "rule",
    KICK = "kick",
    AGENT = "agent",
    USER = "user",
    ORGANIZATION = "organization",
}

export interface AuditLogActor {
    actorId: string | null;
    actorName: string | null;
    actorType: ActorType;
}

export enum AuditExportType {
    TRANSACTIONS = "transactions",
    CHART_OF_ACCOUNTS = "chart-of-accounts",
    BILLS = "bills",
    INVOICES = "invoices",
    NATIVE_INVOICE_PDF = "native-invoice-pdf",
    COUNTERPARTIES = "counterparties",
    PROFIT_LOSS = "profit-loss",
    BALANCE_SHEET = "balance-sheet",
    TRIAL_BALANCE = "trial-balance",
    GENERAL_LEDGER = "general-ledger",
    EXPENSE_BY_VENDOR = "expense-by-vendor",
    CASH_FLOW_STATEMENT = "cash-flow-statement",
    TAX_PACKAGE = "tax-package",
    REPORT_PACKAGE = "report-package",
    BANK_RECONCILIATION = "bank-reconciliation",
    AR_AGING_SUMMARY = "ar-aging-summary",
    AR_AGING_DETAIL = "ar-aging-detail",
    CHECK_DETAIL = "check-detail",
    OWNER_CASH_FLOW = "owner-cash-flow",
    SCHEDULES_REPORT = "schedules-report",
    FORM_1099 = "form-1099",
    FORM_1099_NEC_TRACK1099 = "form-1099-nec-track1099",
}

export enum AuditExportFormat {
    CSV = "csv",
    XLSX = "xlsx",
    PDF = "pdf",
    ZIP = "zip",
}

export type AuditExportFilterValue =
    | string
    | number
    | boolean
    | null
    | AuditExportFilterValue[]
    | { [key: string]: AuditExportFilterValue };

export type AuditExportFilters = Record<string, AuditExportFilterValue>;

/**
 * Transport-specific actor attribution merged into `audit_logs.metadata` JSONB
 * (see `withActorMetadata`). Named fields stay queryable/type-checked; the index
 * signature keeps arbitrary per-insert metadata allowed.
 */
export interface AuditLogMetadata {
    patId?: string;
    authMethod?: AuditAuthMethod;
    mcpOAuthClientId?: string;
    mcpOAuthSubject?: string;
    organizationAccessTokenId?: string;
    exportType?: AuditExportType;
    exportFormat?: AuditExportFormat;
    exportFilters?: AuditExportFilters;
    periodStartDate?: string;
    periodEndDate?: string;
    entityId?: number;
    financialAccountId?: number;
    [key: string]: unknown;
}

export interface DownloadedDocumentMetadata {
    id: number;
    fileName: string;
}

export interface SingleDocumentDownloadMetadata {
    documentId: number;
    fileName: string;
}

export interface BulkDocumentDownloadMetadata {
    documents: DownloadedDocumentMetadata[];
    documentCount: number;
    entityIds: number[];
}

export type DownloadAuditLogMetadata =
    | SingleDocumentDownloadMetadata
    | BulkDocumentDownloadMetadata;

export enum ResourceType {
    TASK = "task",
    CLOSE = "close",
    CLIENT = "client",
    ORGANIZATION_INVITE = "organizationInvite",
    ORGANIZATION_MEMBER = "organizationMember",

    TRANSACTION = "transaction",
    TRANSACTION_RULE = "transactionRule",
    ACCOUNTING_RULE = "accountingRule",
    TRANSFER_RULE = "transferRule",
    SPLIT_RULE = "splitRule",
    TRANSACTION_RULE_GROUP = "transactionRuleGroup",
    ACCOUNT = "account",
    ACCOUNT_GROUP = "accountGroup",
    ENTITY = "entity",
    JOURNAL_ENTRY = "journalEntry",
    MANUAL_JOURNAL_ENTRY = "manualJournalEntry",
    USER = "user",
    CATEGORY = "category",
    FINANCIAL_ACCOUNT = "financialAccount",
    COUNTERPARTY = "counterparty",
    BANK_RECONCILIATION = "bankReconciliation",
    FINANCIAL_DOCUMENT = "financialDocument",
    CLASS = "class",
    BILL = "bill",
    INVOICE = "invoice",
    INVOICE_TEMPLATE = "invoiceTemplate",
    CATALOG_ITEM = "catalogItem",
    NATIVE_INVOICE_DETAIL = "nativeInvoiceDetail",
    ACCRUAL_MATCH = "accrualMatch",
    TRANSACTION_FIELD_REVIEW = "transactionFieldReview",
    chartOfAccountsTemplateApplyEvent = "chartOfAccountsTemplateApplyEvent",
    COUNTERPARTY_FIELD_REVIEW = "counterpartyFieldReview",
    SCHEDULE_TEMPLATE = "scheduleTemplate",
    SCHEDULE_APPLICATION = "scheduleApplication",
    REPORT_SETTINGS = "reportSettings",
}

export const IdFieldToResourceTypeMap: Record<string, ResourceType> = {
    transactionId: ResourceType.TRANSACTION,
    pairedTransferId: ResourceType.TRANSACTION,
    splitId: ResourceType.TRANSACTION,
    splitParentId: ResourceType.TRANSACTION,

    entityId: ResourceType.ENTITY,
    relatedEntityId: ResourceType.ENTITY,
    journalEntryId: ResourceType.JOURNAL_ENTRY,
    manualJournalEntryId: ResourceType.MANUAL_JOURNAL_ENTRY,
    reversedManualJournalEntryId: ResourceType.MANUAL_JOURNAL_ENTRY,
    userId: ResourceType.USER,
    assigneeId: ResourceType.USER,
    updatedById: ResourceType.USER,
    preparerId: ResourceType.USER,
    reviewerId: ResourceType.USER,
    managerId: ResourceType.USER,

    groupId: ResourceType.TRANSACTION_RULE_GROUP,

    categoryId: ResourceType.CATEGORY,
    financialAccountId: ResourceType.FINANCIAL_ACCOUNT,
    counterpartyId: ResourceType.COUNTERPARTY,
    reconciledInId: ResourceType.BANK_RECONCILIATION,
    financialDocumentId: ResourceType.FINANCIAL_DOCUMENT,
    accountId: ResourceType.ACCOUNT,
    accrualAccountId: ResourceType.ACCOUNT,
    suggestedAccrualAccountId: ResourceType.ACCOUNT,
    generalLedgerAccountId: ResourceType.ACCOUNT,
    revenueAccountId: ResourceType.ACCOUNT,
    receivablesAccountId: ResourceType.ACCOUNT,
    parentGroupId: ResourceType.ACCOUNT_GROUP,
    classId: ResourceType.CLASS,
    classIds: ResourceType.CLASS,
    parentClassId: ResourceType.CLASS,
    rootClassId: ResourceType.CLASS,
    billId: ResourceType.BILL,
    invoiceId: ResourceType.INVOICE,
    invoiceTemplateId: ResourceType.INVOICE_TEMPLATE,
    sourceTemplateId: ResourceType.INVOICE_TEMPLATE,
    accrualMatchId: ResourceType.ACCRUAL_MATCH,
    scheduleTemplateId: ResourceType.SCHEDULE_TEMPLATE,
    scheduleApplicationId: ResourceType.SCHEDULE_APPLICATION,
};

export const RULE_RESOURCE_TYPES: ReadonlySet<ResourceType> = new Set([
    ResourceType.TRANSACTION_RULE,
    ResourceType.ACCOUNTING_RULE,
    ResourceType.TRANSFER_RULE,
    ResourceType.SPLIT_RULE,
]);

export const RULE_GROUP_TYPE_TO_RESOURCE_TYPE: Record<
    RuleGroupType,
    ResourceType
> = {
    [RuleGroupType.TRANSACTION]: ResourceType.TRANSACTION_RULE,
    [RuleGroupType.ACCOUNTING]: ResourceType.ACCOUNTING_RULE,
    [RuleGroupType.TRANSFER]: ResourceType.TRANSFER_RULE,
    [RuleGroupType.SPLIT]: ResourceType.SPLIT_RULE,
};

export interface AuditLogCreatedEvent {
    auditLogId: string;
    workspaceId: string;
    resourceType: ResourceType;
    resourceId: string;
    changeType: AuditLogChangeType;
}

export interface RemovedOpeningBalanceLine {
    balance: number;
    description: string | null;
}

export interface RemovedOpeningBalanceRecord {
    date: string;
    clearingAccountId: string;
    clearingBalanceDescription: string | null;
    balance: number;
    lines: RemovedOpeningBalanceLine[];
}

export interface RemovedOpeningBalanceMetadata {
    main: RemovedOpeningBalanceRecord | null;
    adjustment: RemovedOpeningBalanceRecord | null;
}

export const AuditLogSourceTypeMap = {
    ...SQSProcessName,
    ...APIProcessName,
    ...ActivityProcessName,
    ...RPCProcessName,
    ...TestProcessName,
} as const;
